import getBatches from '@cartwhl/common/api/getBatches';
import type { BatchListItem, BatchType } from '@cartwhl/common/api/getBatches';
import getProjects from '@cartwhl/common/api/getProjects';
import getScenes from '@cartwhl/common/api/getScenes';
import {
  type AppletPermissionContext,
  appletEntryIsAllowed,
  evaluatePolicy,
  isAppAllowed,
  isAppEntitlement,
} from '@cartwhl/common/appletPolicy';
import { BaseStore, createSWRCache } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type { AsyncState, Project, Scene } from '@cartwhl/common/types';
import { SceneType } from '@cartwhl/common/types';
import { getStudioUrl } from '@cartwhl/common/utils';

import { applets } from '../../applets';
import type { AppletDefinition } from '../../applets';
import * as globalStores from '../../stores/global';
import {
  type DashboardAppletCardData,
  type DashboardHomeTab,
  type DashboardWorkItem,
  type DashboardWorkItemScope,
  type DashboardWorkItemsData,
  type DashboardWorkTypeFilter,
  isDashboardHomeTab,
} from './types';
import {
  toBatchWorkItem,
  toLegacyProjectWorkItem,
  toSceneWorkItem,
} from './workItemGrouping';

type Deps = { global: typeof globalStores };

type State = {
  showAdmin: boolean;
  userStoreLoading: boolean;
  customerApplets: DashboardAppletCardData[];
  adminApplets: DashboardAppletCardData[];
  accessibleAppletCount: number;
  workItemsLoad: AsyncState<DashboardWorkItemsData>;
  homeTab: DashboardHomeTab;
  workItemsSearchQuery: string;
  workItemsTypeFilter: DashboardWorkTypeFilter;
};

type DashboardWorkItemsLoadContext = {
  loadKey: string;
  apiKeyValue?: string;
  currentOwnerIDs: string[];
  currentUserEmail?: string;
  requestId: number;
};

type DashboardWorkItemsBackgroundLoad = {
  loadContext: DashboardWorkItemsLoadContext;
  legacyProjectNextToken?: string;
  sceneNextToken?: string;
  batchNextPageTokens: Array<{ type: BatchType; nextToken: string }>;
};

const CANVAS_CARD_ORDER = 50;
const LEGACY_PROJECT_HISTORY_PAGE_SIZE = 20;
const LEGACY_PROJECT_HISTORY_MAX_PAGES = 2;
const SCENE_HISTORY_PAGE_SIZE = 20;
const SCENE_HISTORY_MAX_PAGES = 2;
const BATCH_HISTORY_PAGE_SIZE = 20;
const BATCH_HISTORY_MAX_PAGES = 10;
const BATCH_HISTORY_TYPES: BatchType[] = [
  'COMIC',
  'GENERATION',
  'MASCOT',
  'CHARACTER',
];
const DASHBOARD_WORK_ITEMS_TTL_MS = 2 * 60 * 1000;
const dashboardWorkItemsCache = createSWRCache<DashboardWorkItemsData>();

function getHomeTabFromLocation(): DashboardHomeTab {
  const tab = new URLSearchParams(window.location.search).get('tab');
  return isDashboardHomeTab(tab) ? tab : 'mine';
}

function createInitialState(): State {
  return {
    showAdmin: false,
    userStoreLoading: true,
    customerApplets: [],
    adminApplets: [],
    accessibleAppletCount: 0,
    workItemsLoad: { status: 'initial', data: undefined },
    homeTab: getHomeTabFromLocation(),
    workItemsSearchQuery: '',
    workItemsTypeFilter: 'all',
  };
}

const initialState = createInitialState();

class DashboardStore extends BaseStore<State, Deps> {
  // 1. Private fields
  private workItemsRequestId = 0;
  private activeWorkItemsLoadKey: string | null = null;
  private pendingWorkItemsBackgroundLoads = new Map<
    number,
    DashboardWorkItemsBackgroundLoad
  >();

  readonly workItemsLoader = this.createSWRLoader(
    'workItemsLoad',
    (loadContext: DashboardWorkItemsLoadContext) =>
      this.fetchInitialWorkItems(loadContext),
    {
      cache: dashboardWorkItemsCache,
      cacheKey: (loadContext) => loadContext.loadKey,
      ttlMs: DASHBOARD_WORK_ITEMS_TTL_MS,
    },
  );

  // 2. Constructor
  constructor(deps: Deps) {
    super(initialState, deps);
  }

  // 3. Lifecycle
  init() {
    this.state.homeTab = getHomeTabFromLocation();
    this.syncDashboardState();
    void this.loadWorkItemsForCurrentUser();

    this.subscribeTo(
      this.deps.global.authStore.state,
      () => this.syncAndLoadWorkItems(),
      { keys: 'authStatus' },
    );

    this.subscribeTo(
      this.deps.global.userStore.state,
      () => this.syncAndLoadWorkItems(),
      {
        keys: [
          'apiKey',
          'isCartwheeler',
          'loading',
          'organizationInfo',
          'userInfo',
        ],
      },
    );
  }

  reset() {
    this.workItemsRequestId += 1;
    this.activeWorkItemsLoadKey = null;
    this.pendingWorkItemsBackgroundLoads.clear();
    Object.assign(this.state, createInitialState());
  }

  destroy() {
    this.workItemsRequestId += 1;
    this.pendingWorkItemsBackgroundLoads.clear();
    super.destroy();
  }

  // 4. Public methods
  async reloadWorkItems() {
    await this.loadWorkItemsForCurrentUser({ force: true });
  }

  setHomeTab(tab: DashboardHomeTab) {
    this.state.homeTab = tab;
  }

  setWorkItemsSearchQuery(query: string) {
    this.state.workItemsSearchQuery = query;
  }

  setWorkItemsTypeFilter(filter: DashboardWorkTypeFilter) {
    this.state.workItemsTypeFilter = filter;
  }

  // 5. Private methods/getters
  private syncAndLoadWorkItems() {
    this.syncDashboardState();
    void this.loadWorkItemsForCurrentUser();
  }

  private syncDashboardState() {
    const { authStore, userStore } = this.deps.global;
    const { apiKey, isCartwheeler, loading, organizationInfo, userInfo } =
      userStore.state;
    const ctx: AppletPermissionContext = {
      authStatus: authStore.state.authStatus,
      apiKey,
      isCartwheeler,
      userStoreLoading: loading,
      userOrganizationId: userInfo.organizationID ?? '',
    };

    const accessibleApplets = applets.filter(
      (applet) =>
        (!isAppEntitlement(applet.id) ||
          isAppAllowed(applet.id, organizationInfo?.allowedApps)) &&
        appletEntryIsAllowed(evaluatePolicy(applet.policy, ctx)),
    );
    const customerApplets = accessibleApplets
      .filter(
        (applet) =>
          applet.policy.requiresAdmin !== true &&
          applet.dashboard?.hideFromCreateGrid !== true,
      )
      .sort(sortDashboardApplets)
      .map(toDashboardAppletCardData);
    if (isAppAllowed('canvas', organizationInfo?.allowedApps)) {
      customerApplets.splice(
        getCustomCardInsertIndex(accessibleApplets, CANVAS_CARD_ORDER),
        0,
        createCanvasCard(),
      );
    }
    const adminApplets = accessibleApplets
      .filter((applet) => applet.policy.requiresAdmin === true)
      .sort(sortDashboardApplets)
      .map(toDashboardAppletCardData);

    this.state.showAdmin = isCartwheeler === true;
    this.state.userStoreLoading = loading;
    this.state.customerApplets = customerApplets;
    this.state.adminApplets = adminApplets;
    this.state.accessibleAppletCount = accessibleApplets.length;
  }

  private async loadWorkItemsForCurrentUser(options: { force?: boolean } = {}) {
    const { authStore, userStore } = this.deps.global;
    const { apiKey, loading, userInfo } = userStore.state;

    if (authStore.state.authStatus !== 'user') {
      this.workItemsRequestId += 1;
      this.activeWorkItemsLoadKey = null;
      this.pendingWorkItemsBackgroundLoads.clear();
      this.workItemsLoader.reset();
      return;
    }

    if (
      loading ||
      ((apiKey.status === 'initial' || apiKey.status === 'pending') &&
        !apiKey.data)
    ) {
      this.workItemsRequestId += 1;
      this.activeWorkItemsLoadKey = null;
      this.pendingWorkItemsBackgroundLoads.clear();
      this.state.workItemsLoad = {
        status: 'pending',
        data: this.state.workItemsLoad.data,
      };
      return;
    }

    const apiKeyValue = apiKey.data?.apiKey;
    const serviceID = apiKey.data?.serviceID;
    const currentOwnerIDs = createCurrentOwnerIDs(userInfo.userID);
    const currentUserEmail = normalizeOwnerID(userInfo.email);
    const loadKey = `${authStore.state.authStatus}:${userInfo.organizationID ?? ''}:${userInfo.userID ?? ''}:${serviceID ?? ''}:${apiKeyValue ?? 'no-api-key'}`;
    const requestId = this.getWorkItemsRequestId(
      loadKey,
      options.force === true,
    );
    const loadContext: DashboardWorkItemsLoadContext = {
      loadKey,
      apiKeyValue,
      currentOwnerIDs,
      currentUserEmail,
      requestId,
    };
    const loadedData = options.force
      ? await this.workItemsLoader.refresh(loadContext)
      : await this.workItemsLoader.load(loadContext);

    if (!loadedData || requestId !== this.workItemsRequestId) {
      return;
    }

    this.startPendingBackgroundWorkItemsLoad(requestId);
  }

  private async fetchInitialWorkItems(
    loadContext: DashboardWorkItemsLoadContext,
  ) {
    const currentOwnerIDs = new Set(loadContext.currentOwnerIDs);

    const legacyProjectRequest = getProjects(LEGACY_PROJECT_HISTORY_PAGE_SIZE);
    const sceneRequest = loadContext.apiKeyValue
      ? getScenes(loadContext.apiKeyValue, {
          limit: SCENE_HISTORY_PAGE_SIZE,
          sortAscending: false,
        })
      : Promise.resolve({
          data: { scenes: [], nextToken: undefined },
          error: undefined,
        });
    const batchRequests = loadContext.apiKeyValue
      ? BATCH_HISTORY_TYPES.map((type) =>
          getBatches(loadContext.apiKeyValue!, {
            limit: BATCH_HISTORY_PAGE_SIZE,
            type,
          }),
        )
      : [];
    const legacyProjectResultPromise = Promise.allSettled([
      legacyProjectRequest,
    ]).then(([result]) => result);
    const sceneResultPromise = Promise.allSettled([sceneRequest]).then(
      ([result]) => result,
    );
    const batchResultsPromise = Promise.allSettled(batchRequests);

    const [legacyProjectResult, sceneResult, batchResults] = await Promise.all([
      legacyProjectResultPromise,
      sceneResultPromise,
      batchResultsPromise,
    ]);

    if (loadContext.requestId !== this.workItemsRequestId) {
      return { data: undefined };
    }

    const workItems: DashboardWorkItem[] = [];
    const errors: string[] = [];

    if (legacyProjectResult.status === 'fulfilled') {
      if (legacyProjectResult.value.error) {
        errors.push(legacyProjectResult.value.error.message);
      } else {
        workItems.push(
          ...this.toOwnedLegacyProjectWorkItems(
            legacyProjectResult.value.data ?? [],
            currentOwnerIDs,
          ),
        );
      }
    } else {
      errors.push(getUnknownErrorMessage(legacyProjectResult.reason));
    }

    if (sceneResult.status === 'fulfilled') {
      if (sceneResult.value.error) {
        errors.push(sceneResult.value.error.message);
      } else {
        workItems.push(
          ...this.toOwnedSceneWorkItems(
            sceneResult.value.data?.scenes ?? [],
            currentOwnerIDs,
          ),
        );
      }
    } else {
      errors.push(getUnknownErrorMessage(sceneResult.reason));
    }

    const nextPageTokens: Array<{ type: BatchType; nextToken: string }> = [];

    batchResults.forEach((batchResult, index) => {
      const batchType = BATCH_HISTORY_TYPES[index];
      if (!batchType) return;
      if (batchResult.status === 'fulfilled') {
        workItems.push(
          ...this.toOwnedBatchWorkItems(
            batchResult.value.items,
            currentOwnerIDs,
            loadContext.currentUserEmail,
          ),
        );
        if (batchResult.value.nextToken) {
          nextPageTokens.push({
            type: batchType,
            nextToken: batchResult.value.nextToken,
          });
        }
      } else {
        errors.push(getUnknownErrorMessage(batchResult.reason));
      }
    });

    workItems.sort(
      (a, b) => getDateTime(b.updatedAt) - getDateTime(a.updatedAt),
    );

    if (errors.length > 0) {
      this.notifyWorkItemsError(getWorkItemsErrorNotice(errors));
    }

    this.pendingWorkItemsBackgroundLoads.set(loadContext.requestId, {
      loadContext,
      legacyProjectNextToken:
        legacyProjectResult.status === 'fulfilled'
          ? legacyProjectResult.value.nextToken
          : undefined,
      sceneNextToken:
        sceneResult.status === 'fulfilled'
          ? sceneResult.value.data?.nextToken
          : undefined,
      batchNextPageTokens: nextPageTokens,
    });

    return {
      data: {
        items: workItems,
      },
    };
  }

  private getWorkItemsRequestId(loadKey: string, force: boolean) {
    if (force || this.activeWorkItemsLoadKey !== loadKey) {
      this.workItemsRequestId += 1;
      this.activeWorkItemsLoadKey = loadKey;
      this.pendingWorkItemsBackgroundLoads.clear();
    }

    return this.workItemsRequestId;
  }

  private startPendingBackgroundWorkItemsLoad(requestId: number) {
    if (requestId !== this.workItemsRequestId) return;

    const backgroundLoad = this.pendingWorkItemsBackgroundLoads.get(requestId);
    if (!backgroundLoad) return;

    this.pendingWorkItemsBackgroundLoads.delete(requestId);

    if (backgroundLoad.legacyProjectNextToken) {
      void this.loadAdditionalLegacyProjectPages({
        loadContext: backgroundLoad.loadContext,
        nextToken: backgroundLoad.legacyProjectNextToken,
      });
    }

    if (backgroundLoad.sceneNextToken) {
      void this.loadAdditionalScenePages({
        loadContext: backgroundLoad.loadContext,
        nextToken: backgroundLoad.sceneNextToken,
      });
    }

    if (
      backgroundLoad.loadContext.apiKeyValue &&
      backgroundLoad.batchNextPageTokens.length > 0
    ) {
      void this.loadAdditionalBatchPages({
        loadContext: backgroundLoad.loadContext,
        nextPageTokens: backgroundLoad.batchNextPageTokens,
      });
    }
  }

  private toOwnedLegacyProjectWorkItems(
    projects: Project[],
    currentOwnerIDs: Set<string>,
  ): DashboardWorkItem[] {
    return projects.map((project) =>
      toLegacyProjectWorkItem(
        project,
        getWorkItemScope(project.createdBy, currentOwnerIDs),
      ),
    );
  }

  private toOwnedSceneWorkItems(
    scenes: Scene[],
    currentOwnerIDs: Set<string>,
  ): DashboardWorkItem[] {
    return scenes
      .filter((scene) => scene.type === SceneType.MOTION)
      .map((scene) =>
        toSceneWorkItem(
          scene,
          getWorkItemScope(scene.createdByUser, currentOwnerIDs),
        ),
      );
  }

  private toOwnedBatchWorkItems(
    batches: BatchListItem[],
    currentOwnerIDs: Set<string>,
    currentUserEmail: string | undefined,
  ): DashboardWorkItem[] {
    return batches
      .map((batch) =>
        toBatchWorkItem(
          batch,
          getBatchWorkItemScope(batch, currentOwnerIDs, currentUserEmail),
        ),
      )
      .filter(notEmpty);
  }

  private mergeWorkItems(
    loadContext: DashboardWorkItemsLoadContext,
    nextItems: DashboardWorkItem[],
  ) {
    if (nextItems.length === 0) return;

    this.workItemsLoader.updateData([loadContext], (currentData) => {
      if (!currentData) return undefined;

      return {
        ...currentData,
        items: mergeDashboardWorkItems(currentData.items, nextItems),
      };
    });
  }

  private async loadAdditionalBatchPages(options: {
    loadContext: DashboardWorkItemsLoadContext;
    nextPageTokens: Array<{ type: BatchType; nextToken: string }>;
  }) {
    await Promise.all(
      options.nextPageTokens.map(({ type, nextToken }) =>
        this.loadAdditionalBatchPagesForType({
          loadContext: options.loadContext,
          type,
          nextToken,
        }),
      ),
    );
  }

  private async loadAdditionalLegacyProjectPages(options: {
    loadContext: DashboardWorkItemsLoadContext;
    nextToken: string;
  }) {
    const currentOwnerIDs = new Set(options.loadContext.currentOwnerIDs);
    let nextToken: string | undefined = options.nextToken;

    for (
      let pageIndex = 2;
      pageIndex <= LEGACY_PROJECT_HISTORY_MAX_PAGES && nextToken;
      pageIndex += 1
    ) {
      const {
        data,
        error,
        nextToken: resultNextToken,
      } = await getProjects(LEGACY_PROJECT_HISTORY_PAGE_SIZE, nextToken);

      if (options.loadContext.requestId !== this.workItemsRequestId) return;

      if (error) {
        this.notifyWorkItemsError(error.message);
        return;
      }

      this.mergeWorkItems(
        options.loadContext,
        this.toOwnedLegacyProjectWorkItems(data ?? [], currentOwnerIDs),
      );
      nextToken = resultNextToken;
    }
  }

  private async loadAdditionalScenePages(options: {
    loadContext: DashboardWorkItemsLoadContext;
    nextToken: string;
  }) {
    const currentOwnerIDs = new Set(options.loadContext.currentOwnerIDs);
    let nextToken: string | undefined = options.nextToken;

    for (
      let pageIndex = 2;
      pageIndex <= SCENE_HISTORY_MAX_PAGES && nextToken;
      pageIndex += 1
    ) {
      if (!options.loadContext.apiKeyValue) return;

      const { data, error } = await getScenes(options.loadContext.apiKeyValue, {
        limit: SCENE_HISTORY_PAGE_SIZE,
        nextToken,
        sortAscending: false,
      });

      if (options.loadContext.requestId !== this.workItemsRequestId) return;

      if (error) {
        this.notifyWorkItemsError(error.message);
        return;
      }

      this.mergeWorkItems(
        options.loadContext,
        this.toOwnedSceneWorkItems(data?.scenes ?? [], currentOwnerIDs),
      );
      nextToken = data?.nextToken;
    }
  }

  private async loadAdditionalBatchPagesForType(options: {
    loadContext: DashboardWorkItemsLoadContext;
    type: BatchType;
    nextToken: string;
  }) {
    if (!options.loadContext.apiKeyValue) return;

    const currentOwnerIDs = new Set(options.loadContext.currentOwnerIDs);
    let nextToken: string | undefined = options.nextToken;

    for (
      let pageIndex = 2;
      pageIndex <= BATCH_HISTORY_MAX_PAGES && nextToken;
      pageIndex += 1
    ) {
      let result: Awaited<ReturnType<typeof getBatches>>;
      try {
        result = await getBatches(options.loadContext.apiKeyValue, {
          limit: BATCH_HISTORY_PAGE_SIZE,
          nextToken,
          type: options.type,
        });
      } catch (error) {
        this.notifyWorkItemsError(getUnknownErrorMessage(error));
        return;
      }

      if (options.loadContext.requestId !== this.workItemsRequestId) return;

      this.mergeWorkItems(
        options.loadContext,
        this.toOwnedBatchWorkItems(
          result.items,
          currentOwnerIDs,
          options.loadContext.currentUserEmail,
        ),
      );
      nextToken = result.nextToken;
    }
  }

  private notifyWorkItemsError(message: string) {
    this.deps.global.noticesStore.error(
      message.trim() || 'Failed to load recent work.',
    );
  }
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value != null;
}

function getWorkItemKey(item: DashboardWorkItem): string {
  return `${item.kind}:${item.id}`;
}

function createCurrentOwnerIDs(userID: string | undefined): string[] {
  return [normalizeOwnerID(userID)].filter(notEmpty);
}

function mergeDashboardWorkItems(
  currentItems: DashboardWorkItem[],
  nextItems: DashboardWorkItem[],
): DashboardWorkItem[] {
  const workItemsByKey = new Map(
    currentItems.map((item) => [getWorkItemKey(item), item]),
  );
  nextItems.forEach((item) => {
    workItemsByKey.set(getWorkItemKey(item), item);
  });

  return [...workItemsByKey.values()].sort(
    (a, b) => getDateTime(b.updatedAt) - getDateTime(a.updatedAt),
  );
}

function getBatchWorkItemScope(
  batch: BatchListItem,
  currentOwnerIDs: Set<string>,
  currentUserEmail: string | undefined,
): DashboardWorkItemScope {
  const normalizedCreatedByUser = normalizeOwnerID(batch.createdByUser);

  if (normalizedCreatedByUser && currentOwnerIDs.has(normalizedCreatedByUser)) {
    return 'mine';
  }

  if (
    currentUserEmail &&
    batch.emailSubscribers?.some(
      (email) => normalizeOwnerID(email) === currentUserEmail,
    )
  ) {
    return 'mine';
  }

  // API-created batches (no createdByUser) belong to the current account.
  if (!normalizedCreatedByUser) {
    return 'mine';
  }

  return 'team';
}

function getWorkItemScope(
  createdByUser: string | undefined,
  currentOwnerIDs: Set<string>,
): DashboardWorkItemScope {
  const normalizedCreatedByUser = normalizeOwnerID(createdByUser);

  // Missing createdByUser means API-created; treat as the current account's work.
  if (!normalizedCreatedByUser || currentOwnerIDs.size === 0) {
    return 'mine';
  }

  return currentOwnerIDs.has(normalizedCreatedByUser) ? 'mine' : 'team';
}

function normalizeOwnerID(value: string | undefined): string | undefined {
  const normalized = value?.trim().toLowerCase();
  return normalized || undefined;
}

function getDateTime(dateValue: string): number {
  const dateTime = new Date(dateValue).getTime();
  return Number.isNaN(dateTime) ? 0 : dateTime;
}

function getUnknownErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Failed to load recent work.';
}

function getWorkItemsErrorNotice(errors: string[]): string {
  const uniqueErrors = [
    ...new Set(errors.map((error) => error.trim()).filter(Boolean)),
  ];

  if (uniqueErrors.length === 1) {
    return uniqueErrors[0];
  }

  return 'Failed to load recent work.';
}

function getCustomCardInsertIndex(
  applets: AppletDefinition[],
  cardOrder: number,
) {
  const orderedCustomerApplets = applets
    .filter(
      (applet) =>
        applet.policy.requiresAdmin !== true &&
        applet.dashboard?.hideFromCreateGrid !== true,
    )
    .sort(sortDashboardApplets);

  const nextIndex = orderedCustomerApplets.findIndex(
    (applet) =>
      (applet.dashboard?.order ?? Number.MAX_SAFE_INTEGER) > cardOrder,
  );

  return nextIndex === -1 ? orderedCustomerApplets.length : nextIndex;
}

function createCanvasCard(): DashboardAppletCardData {
  return {
    id: 'canvas',
    path: getStudioUrl(),
    external: true,
    title: 'Canvas',
    description: 'Create and prototype in our comprehensive suite.',
    videoSrc:
      'https://resources.getcartwheel.com/share/demo-videos/app_launcher_canvas_2.webm',
  };
}

function sortDashboardApplets(a: AppletDefinition, b: AppletDefinition) {
  const orderA = a.dashboard?.order ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.dashboard?.order ?? Number.MAX_SAFE_INTEGER;

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  const titleA = a.dashboard?.title ?? a.label;
  const titleB = b.dashboard?.title ?? b.label;

  return titleA.localeCompare(titleB);
}

function toDashboardAppletCardData(
  applet: AppletDefinition,
): DashboardAppletCardData {
  return {
    id: applet.id,
    path: applet.path,
    title: applet.dashboard?.title ?? applet.label,
    description: applet.dashboard?.description ?? applet.description ?? '',
    imageSrc: applet.dashboard?.imageSrc,
    imageAlt: applet.dashboard?.imageAlt,
    videoSrc: applet.dashboard?.videoSrc,
    badge: applet.dashboard?.badge,
    disabled: applet.dashboard?.disabled,
  };
}

export const { Provider, useStore, withProvider, Context } =
  createStoreProvider({
    Store: DashboardStore,
    displayName: 'DashboardStore',
    staticDeps: { global: globalStores },
  });

export type { DashboardStore };
